import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Zap } from "lucide-react";
import { useLang, RESERVE_URL } from "../context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1745329532593-53a9ec306787?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxwZXJzb25hbCUyMHRyYWluZXIlMjBneW18ZW58MHx8fHwxNzc3MDY4NDY4fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 180]);
  const opacity = useTransform(scrollY, [700, 1400], [1, 0.2]);
  const scale = useTransform(scrollY, [0, 500], [1.05, 1.2]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden bg-black flex items-end">
      {/* Parallax background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="XPRO Fitness Vidreres"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
      </motion.div>

      {/* Vertical guide */}
      <div className="absolute top-0 bottom-0 left-6 sm:left-10 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent pointer-events-none" />
      <div className="absolute top-0 bottom-0 right-6 sm:right-10 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent pointer-events-none" />

      {/* Right-aligned vertical meta */}
      <div className="hidden md:flex absolute right-10 top-1/2 -translate-y-1/2 rotate-90 origin-right text-[11px] tracking-[0.4em] font-semibold text-white/50 uppercase">
        VIDRERES · GIRONA · EST. 2025
      </div>

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-28 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex items-center gap-3 text-[11px] tracking-[0.3em] font-bold uppercase text-[#FFCC00] mb-8"
          data-testid="hero-overline"
        >
          <Zap size={14} className="fill-[#FFCC00]" />
          {t.hero.overline}
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-white leading-[0.86] text-[16vw] md:text-[11vw] lg:text-[9rem] xl:text-[11rem]"
        >
          <motion.span
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="block"
          >
            {t.hero.headline1}
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="block pl-[10vw] md:pl-[12vw]"
          >
            {t.hero.headline2}
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="block text-[#FFCC00]"
          >
            {t.hero.headline3}
          </motion.span>
        </h1>

        <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            data-testid="hero-subheadline"
            className="md:col-span-6 text-base md:text-lg text-white/75 max-w-xl font-body"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="md:col-span-6 flex flex-col sm:flex-row gap-3 md:justify-end"
          >
            <a
              href={RESERVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-3 px-8 py-5 bg-[#FFCC00] text-black font-bold text-xs tracking-[0.25em] uppercase hover:bg-white transition-all hover:translate-y-[-2px]"
            >
              {t.hero.cta_primary}
              <span className="inline-block w-6 h-px bg-black group-hover:w-10 transition-all" />
            </a>
            <a
              data-testid="hero-cta-secondary"
              href="#facilities"
              className="inline-flex items-center justify-center gap-3 px-8 py-5 border border-white/25 text-white font-bold text-xs tracking-[0.25em] uppercase hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
            >
              {t.hero.cta_secondary}
            </a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.a
          href="#stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-3 text-white/50"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ArrowDown size={16} />
          </motion.div>
        </motion.a>
      </motion.div>
    </section>
  );
}
